import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { linkMentions, type MentionPerson, mentionUrlTransform } from "@/lib/mentions";
import { cn } from "@/lib/utils";

/** Shared typography for rendered markdown and the description editor. */
export const markdownProse = [
  "space-y-3 text-sm leading-relaxed break-words",
  "[&_h1]:text-xl [&_h1]:font-semibold [&_h1]:leading-snug [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:leading-snug [&_h3]:text-base [&_h3]:font-semibold",
  "[&_ul:not([data-type=taskList])]:list-disc [&_ul:not([data-type=taskList])]:pl-5",
  "[&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-0.5",
  "[&_ul[data-type=taskList]]:list-none [&_ul[data-type=taskList]]:pl-0.5",
  "[&_li[data-type=taskItem]]:flex [&_li[data-type=taskItem]]:items-start [&_li[data-type=taskItem]]:gap-2",
  "[&_li[data-type=taskItem]>label]:mt-0.5 [&_li[data-type=taskItem]>div]:min-w-0 [&_li[data-type=taskItem]>div]:flex-1",
  "[&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-xs",
  "[&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:border [&_pre]:bg-muted/50 [&_pre]:p-3 [&_pre_code]:bg-transparent [&_pre_code]:p-0",
  "[&_a]:text-primary [&_a]:underline [&_a]:underline-offset-2",
  "[&_blockquote]:border-l-2 [&_blockquote]:pl-3 [&_blockquote]:text-muted-foreground",
  "[&_hr]:border-border",
  "[&_table]:w-full [&_table]:border-collapse [&_th]:border [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_td]:border [&_td]:px-2 [&_td]:py-1 [&_td]:min-w-36 [&_th]:min-w-36",
  "[&_img]:max-h-96 [&_img]:rounded-md [&_img]:border",
].join(" ");

const COMPONENTS: Components = {
  // A wide table scrolls inside its own box instead of widening the page.
  table: ({ node: _node, ...props }) => (
    <div className="overflow-x-auto">
      <table {...props} />
    </div>
  ),
};

const MENTION_COMPONENTS: Components = {
  ...COMPONENTS,
  a: ({ node: _node, href, children }) => {
    if (href?.startsWith("mention:")) {
      return (
        <span
          className="rounded-sm bg-primary/10 px-0.5 font-medium text-primary"
          data-mention={href.slice(8)}
        >
          {children}
        </span>
      );
    }
    if (!href) return <span>{children}</span>;
    return (
      <a href={href} rel="noreferrer" target="_blank">
        {children}
      </a>
    );
  },
};

/** Markdown for issue descriptions and comments, styled to match the dashboard. */
export function Markdown({
  children,
  className,
  mentions,
}: {
  children: string;
  className?: string;
  /** When set, `@Name` in the text is highlighted for these people. */
  mentions?: MentionPerson[];
}) {
  const source = mentions?.length ? linkMentions(children, mentions) : children;
  return (
    <div className={cn(markdownProse, className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={mentions ? MENTION_COMPONENTS : COMPONENTS}
        urlTransform={mentions ? mentionUrlTransform : undefined}
      >
        {source}
      </ReactMarkdown>
    </div>
  );
}
